import { useEffect, useRef, useState } from "react";
import Countdown, { CountdownApi, CountdownRenderProps } from "react-countdown";

import { env } from "~/env";
import { tw } from "~/utils";

interface ResendEmailProps {
  isPending: boolean;
  onClick: () => void;
  isSuccess: boolean;
}

export const ResendEmail = (props: ResendEmailProps) => {
  const { isPending, isSuccess } = props;

  const [key, setKey] = useState(1);
  const [date, setDate] = useState(Date.now() + env.VITE_EMAIL_THROTTLE * 1000);
  const [resendAvailable, setResendAvailable] = useState(false);

  const resent = key !== 1;

  const countdownApiRef = useRef<CountdownApi | null>(null);

  const renderer = ({ minutes, seconds, completed }: CountdownRenderProps) => {
    if (completed || isPending) {
      if (completed && !resendAvailable) {
        setResendAvailable(true);
      }
      return <ResendButton {...props} />;
    }

    const pad = (num: number) => String(num).padStart(2, "0");
    const timer = `${pad(minutes)}:${pad(seconds)}`;

    const finalCopy = resent
      ? "If you don't receive it, you can resend it in"
      : "Resend in";

    return (
      <span className="font-sans italic text-blue-900">
        {finalCopy}
        {
          <>
            {/* Visually hidden but accessible version */}
            <span aria-live="off" className="sr-only">
              {" "}
              {timer}
            </span>

            {/* Visually visible version, hidden from screen readers */}
            <span aria-hidden="true" className="ml-1 inline-block">
              {timer}
            </span>
          </>
        }
      </span>
    );
  };

  const setRef = (countdown: Countdown | null): void => {
    if (countdown) {
      const api = countdown.getApi();
      countdownApiRef.current = api;
      api.start();
    }
  };

  useEffect(() => {
    if (isSuccess) {
      const newDate = Date.now() + env.VITE_EMAIL_THROTTLE * 1000;
      setDate(newDate);
      setKey((prev) => prev + 1);
      setResendAvailable(false);
    }
  }, [isSuccess]);

  const copy = resent
    ? "It may take a few seconds for the email to reach your inbox."
    : "Didn't receive the email?";

  return (
    <section className="w-full">
      <div
        role="alert"
        aria-live="polite"
        className="flex w-full flex-col gap-2 rounded-md bg-green-100 p-4"
      >
        <strong className="block font-sans font-semibold text-blue-900">
          {copy}
        </strong>

        {/* Countdown with accessibility-safe renderer */}
        <Countdown
          key={key}
          autoStart={false}
          date={date}
          renderer={renderer}
          ref={setRef}
        />

        {/* Optional: SR-only alert when resend is available */}
        {resendAvailable && (
          <span role="alert" className="sr-only">
            You can resend the email now.
          </span>
        )}
      </div>
    </section>
  );
};

const ResendButton = ({ onClick, isPending }: ResendEmailProps) => {
  return (
    <>
      {/* Always render the live region */}
      <span role="status" aria-live="polite" className="sr-only">
        {isPending ? "Sending email..." : ""}
      </span>

      <button
        onClick={onClick}
        disabled={isPending}
        aria-busy={isPending}
        role="button"
        aria-label={isPending ? "Sending email, please wait" : "Resend email"}
        className={tw(
          "w-max text-left font-sans italic text-blue-900",
          !isPending && "cursor-pointer underline",
        )}
      >
        {isPending ? "Sending email..." : "Click here to resend it."}
      </button>
    </>
  );
};
